/**
 * Email-based access control for menu visibility and route protection.
 *
 * Users NOT listed here (or listed with `fullAccess: true`) see everything.
 * Restricted users only see the departments/menus specified in their `allow` map.
 *
 * Format:
 *   'email@domain.com': {
 *     allow: {
 *       'Department Name': ['Menu Item 1', 'Menu Item 2'],   // specific menus only
 *       'Other Department': '*',                              // all menus in department
 *     }
 *   }
 */
const restrictedUsers = {
  'isadmin@krotrust.org': {
    allow: {
      'Bread': ['Operations Management'],
      'Data': ['Stewardship Intelligence'],
      'KRO Exchange': '*',
      // Legacy department name — no longer in `departments`, so it renders no
      // sidebar entry. Kept so pre-move /settlement-hub links stay reachable.
      'Settlement Hub': '*',
    }
  },
};

const KEY_GATE_SECRET = process.env.REACT_APP_RSID_KEY;

const keyGatedUsers = [
  'isadmin@krotrust.org',
  'oleh.shutiak@krotrust.org',
];

const keyGatedPaths = [
  '/kro-exchange/settlement-hub',
  '/settlement-hub',
  '/data/stewardship-intelligence',
];

export function requiresKeyGate(email, path) {
  if (!email || !path) return false;
  const normalizedEmail = email.toLowerCase().trim();
  if (!keyGatedUsers.includes(normalizedEmail)) return false;
  return keyGatedPaths.some(gated => path === gated || path.startsWith(gated + '/'));
}

export function validateKeyGate(key) {
  if (!KEY_GATE_SECRET || !key) return false;
  return key === KEY_GATE_SECRET;
}

const SESSION_KEY = 'kro_rsid_verified';

/**
 * Returns the access policy for a given email.
 * If no restriction exists, returns { fullAccess: true }.
 */
export function getAccessPolicy(email) {
  if (!email) return { fullAccess: false, allow: {} };
  const key = email.toLowerCase().trim();
  const policy = restrictedUsers[key];
  if (!policy) return { fullAccess: true };
  return { fullAccess: false, allow: policy.allow };
}

/**
 * Filter the departments list based on the user's access policy.
 */
export function filterDepartments(allDepartments, policy) {
  if (policy.fullAccess) return allDepartments;
  return allDepartments.filter(dept => dept in policy.allow);
}

/**
 * Filter the menus object for a given department based on the user's access policy.
 * Returns a filtered copy of the menus for that department.
 */
export function filterMenus(dept, deptMenus, policy) {
  if (policy.fullAccess) return deptMenus;
  const allowed = policy.allow[dept];
  if (!allowed) return {};
  if (allowed === '*') return deptMenus;
  const filtered = {};
  for (const menuKey of Object.keys(deptMenus)) {
    if (allowed.includes(menuKey)) {
      filtered[menuKey] = deptMenus[menuKey];
    }
  }
  return filtered;
}

/**
 * Check if a given route path is allowed for the user.
 * Uses a simple prefix match against allowed department/menu paths.
 */
export function isRouteAllowed(path, policy, pathForFn) {
  if (policy.fullAccess) return true;
  if (!path || path === '/' || path === '/trust-tech-studio' || path === '/manage-your-ledger' || path === '/managed-governance' || path === '/subscription') return true;

  for (const dept of Object.keys(policy.allow)) {
    const deptPath = pathForFn(dept);
    if (path === deptPath || path.startsWith(deptPath + '/')) {
      const allowed = policy.allow[dept];
      if (allowed === '*') return true;
      for (const menuItem of allowed) {
        const menuPath = pathForFn(dept, menuItem);
        if (path === menuPath || path.startsWith(menuPath + '/')) return true;
      }
    }
  }
  return false;
}

export function isKeyGateVerified() {
  return sessionStorage.getItem(SESSION_KEY) === 'true';
}

export function setKeyGateVerified() {
  sessionStorage.setItem(SESSION_KEY, 'true');
}
